/* Module Emplacement — 4 types de panneaux, remplissage selon contrats, carte schématique. */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { MapPin, X, Building2, FileText, Image as ImageIcon, Maximize2, Pencil, Trash2, Check, Plus, RefreshCw } from 'lucide-react';
import { TYPE_DEFS, capaciteFor, computeOccupation, officialByType, occupationEvolution } from '../components/emplacement/occupation';
import {
  TypeCards, SectionKpis, FillRow, TableHeader, SchematicMap, EvolutionCard, TauxCard, ARetenirCard,
} from '../components/emplacement/EmplacementBlocks';

const API = process.env.REACT_APP_BACKEND_URL;

const typeColor = (t) => {
  const s = (t || '').toLowerCase();
  if (s.includes('poteau')) return '#0EA5E9';
  if (s.includes('led')) return '#F59E0B';
  if (s.includes('grand')) return '#8B5CF6';
  return '#22C55E';
};

const typeBadgeClass = (t) => {
  const s = (t || '').toLowerCase();
  if (s.includes('poteau')) return 'bg-cyan-100 text-cyan-700 border-cyan-300';
  if (s.includes('led')) return 'bg-amber-100 text-amber-700 border-amber-300';
  if (s.includes('grand')) return 'bg-violet-100 text-violet-700 border-violet-300';
  return 'bg-emerald-100 text-emerald-700 border-emerald-300';
};

const TYPE_DB = { deroulant: 'Panneau déroulant', led: 'Panneau LED', grand: 'Grand format', poteau: 'Panneau poteau' };
const EXAMPLE_NAME = { deroulant: 'Emplacement déroulant', led: 'Panneau LED', grand: 'Panneau grand format', poteau: 'Avenue' };

export default function Emplacement({ user, onLogout }) {
  const [emplacements, setEmplacements] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [activeType, setActiveType] = useState('deroulant');

  const loadAll = async () => {
    try {
      const [eRes, rRes, cRes] = await Promise.all([
        axios.get(`${API}/api/emplacements`),
        axios.get(`${API}/api/emplacement-reservations`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/contrats`).catch(() => ({ data: [] })),
      ]);
      setEmplacements(eRes.data || []);
      setReservations(rRes.data || []);
      setContrats(cRes.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const onFocus = () => loadAll();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line
  }, []);

  const [rowEditingId, setRowEditingId] = useState(null);
  const [rowEditName, setRowEditName] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newEmp, setNewEmp] = useState({ nom: '', type_panneau: 'Panneau déroulant' });
  const [creating, setCreating] = useState(false);

  const submitAddEmp = async () => {
    if (!newEmp.nom.trim()) { toast.error('Le nom est requis'); return; }
    setCreating(true);
    try {
      const nextNum = Math.max(0, ...emplacements.map(e => e.numero || 0)) + 1;
      await axios.post(`${API}/api/emplacements`, {
        nom: newEmp.nom.trim(), type_panneau: newEmp.type_panneau, numero: nextNum,
        quartier: 'Bangui', commune: 'Bangui', latitude: 4.3947, longitude: 18.5582,
        affiches_disponibles: capaciteFor(newEmp), statut: 'Actif',
      });
      toast.success('Emplacement ajouté');
      setShowAdd(false);
      setNewEmp({ nom: '', type_panneau: 'Panneau déroulant' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de création'); }
    finally { setCreating(false); }
  };

  const startRowEdit = (ev, e) => { ev.stopPropagation(); setRowEditingId(e.id); setRowEditName(e.nom); };
  const cancelRowEdit = (ev) => { ev?.stopPropagation?.(); setRowEditingId(null); setRowEditName(''); };
  const saveRowEdit = async (ev, e) => {
    ev.stopPropagation();
    if (!rowEditName.trim()) { toast.error('Le nom est requis'); return; }
    try {
      await axios.put(`${API}/api/emplacements/${e.id}`, { nom: rowEditName.trim() });
      toast.success('Emplacement renommé');
      setRowEditingId(null); setRowEditName('');
      loadAll();
    } catch { toast.error('Erreur de mise à jour'); }
  };
  const deleteRow = async (ev, e) => {
    ev.stopPropagation();
    if (!window.confirm(`Supprimer définitivement « ${e.nom} » ?\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/api/emplacements/${e.id}`);
      toast.success('Emplacement supprimé');
      if (selectedId === e.id) setSelectedId(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de suppression'); }
  };

  const syncOfficiels = async () => {
    if (!window.confirm('Synchroniser vers 10 déroulants + 3 LED + 1 grand format + 4 poteaux ? Aucune donnée existante ne sera supprimée.')) return;
    try {
      const r = await axios.post(`${API}/api/emplacements/sync-officiels`);
      toast.success(r.data?.message || 'Sync effectuée');
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur sync'); }
  };

  const remplissage = useMemo(() => computeOccupation(contrats, emplacements), [contrats, emplacements]);
  const byType = useMemo(() => officialByType(emplacements), [emplacements]);

  // Lignes affichées : les emplacements officiels du type, complétées par des « exemples » jusqu'au nombre attendu
  const rowsByType = useMemo(() => {
    const out = {};
    for (const d of TYPE_DEFS) {
      const real = byType[d.key].map((e, i) => ({ key: e.id, e, num: i + 1, name: e.nom, occ: remplissage.get(e.id) || 0, cap: capaciteFor(e) }));
      const examples = Array.from({ length: d.max - real.length }, (_, j) => {
        const num = real.length + j + 1;
        return { key: `ex-${d.key}-${num}`, num, name: `${EXAMPLE_NAME[d.key]} ${num}`, occ: 0, cap: capaciteFor({ type_panneau: TYPE_DB[d.key] }), isExample: true };
      });
      out[d.key] = [...real, ...examples];
    }
    return out;
  }, [byType, remplissage]);

  const typeItems = TYPE_DEFS.map((d) => {
    const rows = rowsByType[d.key];
    return { ...d, count: d.max, occupied: rows.reduce((s, r) => s + r.occ, 0), capacity: rows.reduce((s, r) => s + r.cap, 0) };
  });
  const totalOfficial = typeItems.reduce((s, t) => s + t.count, 0);
  const active = typeItems.find((t) => t.key === activeType);
  const activeRows = rowsByType[activeType];
  const points = useMemo(() => occupationEvolution(contrats, emplacements, byType[activeType]), [contrats, emplacements, byType, activeType]);
  const hasExamples = activeRows.some((r) => r.isExample);

  const selected = emplacements.find((e) => e.id === selectedId);
  const selectedOccupied = selected ? (remplissage.get(selected.id) || 0) : 0;
  const selectedRes = selected ? reservations.filter((r) => (r.emplacement_id === selected.id || r.emplacement_nom === selected.nom)) : [];
  const selectedContrat = selected ? contrats.find((c) => (c.emplacements_ids || []).includes(selected.id)
    || (c.emplacement_nom === selected.nom)
    || (Array.isArray(c.lignes) && c.lignes.some((l) => (l.emplacements || []).includes(selected.nom) || l.emplacement === selected.nom))) : null;

  const rowName = (r) => (rowEditingId === r.e?.id ? (
    <input value={rowEditName} onChange={(ev) => setRowEditName(ev.target.value)} onClick={(ev) => ev.stopPropagation()} autoFocus
      onKeyDown={(ev) => { if (ev.key === 'Enter') saveRowEdit(ev, r.e); if (ev.key === 'Escape') cancelRowEdit(ev); }}
      className="w-full h-8 rounded-md border border-blue-300 px-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-blue-300"
      data-testid={`row-edit-input-${r.num}`} />
  ) : null);

  const rowActions = (r) => {
    if (r.isExample) return null;
    if (rowEditingId === r.e.id) {
      return (
        <>
          <button className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50" onClick={(ev) => saveRowEdit(ev, r.e)} data-testid={`row-save-${r.num}`}><Check className="h-4 w-4" /></button>
          <button className="p-1.5 rounded text-slate-500 hover:bg-slate-100" onClick={cancelRowEdit} data-testid={`row-cancel-${r.num}`}><X className="h-4 w-4" /></button>
        </>
      );
    }
    return (
      <>
        <button className="p-1.5 rounded text-[#2448d8] hover:bg-blue-50" onClick={(ev) => startRowEdit(ev, r.e)} title="Modifier le nom" data-testid={`row-edit-${r.num}`}><Pencil className="h-4 w-4" /></button>
        <button className="p-1.5 rounded text-rose-600 hover:bg-rose-50" onClick={(ev) => deleteRow(ev, r.e)} title="Supprimer" data-testid={`row-delete-${r.num}`}><Trash2 className="h-4 w-4" /></button>
      </>
    );
  };

  const body = (
    <div className="space-y-5" data-testid="emplacement-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Module Emplacement</h1>
          <p className="text-slate-600 text-sm mt-1">Gérez les {totalOfficial} emplacements publicitaires et suivez le remplissage de chaque panneau selon les contrats</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === 'admin' && (
            <Button variant="outline" onClick={syncOfficiels} className="gap-2 border-blue-200 text-[#2448d8] font-bold hover:bg-blue-50" data-testid="btn-sync-emplacements">
              <RefreshCw className="h-4 w-4" /> Synchroniser (10+3+1+4)
            </Button>
          )}
          <Button onClick={() => { setShowAdd((v) => !v); setNewEmp({ nom: '', type_panneau: TYPE_DB[activeType] }); }} className="bg-[#2448d8] hover:bg-[#1f3fc7] gap-2 font-bold" data-testid="add-emplacement-btn">
            <Plus className="h-4 w-4" /> Ajouter un emplacement
          </Button>
        </div>
      </div>

      {showAdd && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 px-4 py-3 flex items-center gap-2 flex-wrap" data-testid="add-emp-form">
          <input autoFocus value={newEmp.nom} onChange={(ev) => setNewEmp((p) => ({ ...p, nom: ev.target.value }))}
            onKeyDown={(ev) => { if (ev.key === 'Enter') submitAddEmp(); if (ev.key === 'Escape') setShowAdd(false); }}
            placeholder="Nom du nouvel emplacement (ex: Rond-point Marabena)"
            className="flex-1 min-w-[220px] h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" data-testid="new-emp-nom" />
          <select value={newEmp.type_panneau} onChange={(ev) => setNewEmp((p) => ({ ...p, type_panneau: ev.target.value }))}
            className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="new-emp-type">
            {Object.values(TYPE_DB).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Button size="sm" onClick={submitAddEmp} disabled={creating} className="bg-[#2448d8] hover:bg-[#1f3fc7] gap-1 h-9" data-testid="new-emp-save">
            <Check className="h-4 w-4" /> {creating ? 'Création…' : 'Créer'}
          </Button>
          <Button size="sm" variant="ghost" className="h-9" onClick={() => setShowAdd(false)} data-testid="new-emp-cancel">Annuler</Button>
        </div>
      )}

      <TypeCards items={typeItems} active={activeType} onSelect={setActiveType} total={totalOfficial} />

      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_2px_10px_rgba(15,23,42,0.04)] p-5" data-testid={`type-section-${activeType}`}>
        <h2 className="text-lg font-bold text-slate-900">{active.label}</h2>
        <p className="text-sm text-slate-500">{active.count} emplacement{active.count > 1 ? 's' : ''} · liste et remplissage selon les contrats</p>
        <SectionKpis capacity={active.capacity} occupied={active.occupied} />
        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-5 mt-4">
          <div className="min-w-0 overflow-x-auto">
            <div className="min-w-[560px]">
              <TableHeader />
              {loading && <div className="p-4 text-sm text-slate-500">Chargement...</div>}
              {!loading && activeRows.map((r) => (
                <FillRow key={r.key} e={r.e || { nom: r.name }} num={r.num} occ={r.occ} cap={r.cap} isExample={r.isExample}
                  onOpen={() => setSelectedId(r.e.id)} nameSlot={rowName(r)} actions={rowActions(r)} />
              ))}
            </div>
            {hasExamples && <p className="text-xs text-slate-400 mt-3">Les emplacements marqués « exemple » sont des données fictives à remplacer par les vôtres.</p>}
          </div>
          <SchematicMap title={active.label} rows={activeRows} onOpen={(e) => setSelectedId(e.id)} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] gap-5">
        <EvolutionCard points={points} />
        <TauxCard occupied={active.occupied} capacity={active.capacity} />
        <ARetenirCard rows={activeRows} points={points} occupied={active.occupied} capacity={active.capacity} />
      </div>

      {selected && (
        <EmplacementDetailPanel
          emplacement={selected}
          occupied={selectedOccupied}
          reservations={selectedRes}
          contrat={selectedContrat}
          onClose={() => setSelectedId(null)}
          onReload={loadAll}
        />
      )}
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout} currentPage="emplacement">
      {body}
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────
// Panneau latéral fixe à droite de l'écran (comme sur la maquette)
// ─────────────────────────────────────────────────────────────
function EmplacementDetailPanel({ emplacement: e, occupied, reservations, contrat, onClose, onReload }) {
  const cap = capaciteFor(e);
  const restant = Math.max(0, cap - occupied);
  const pct = Math.min(100, (occupied / cap) * 100);
  const color = typeColor(e.type_panneau);
  const [affiches, setAffiches] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Édition inline du nom + type
  const [editing, setEditing] = useState(false);
  const [editNom, setEditNom] = useState(e.nom);
  const [editType, setEditType] = useState(e.type_panneau);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Charge la liste des affiches du panneau (la plus récente = actuelle)
  useEffect(() => {
    axios.get(`${API}/api/emplacements/${e.id}/affiches`)
      .then((r) => setAffiches(r.data || []))
      .catch(() => setAffiches([]));
    setEditNom(e.nom);
    setEditType(e.type_panneau);
    setEditing(false);
  }, [e.id, e.nom, e.type_panneau]);

  const saveEdit = async () => {
    if (!editNom.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/emplacements/${e.id}`, { nom: editNom.trim(), type_panneau: editType });
      toast.success('Emplacement mis à jour');
      setEditing(false);
      onReload?.();
    } catch { toast.error('Erreur de mise à jour'); }
    finally { setSaving(false); }
  };

  const deleteEmp = async () => {
    if (!window.confirm(`Supprimer définitivement l'emplacement « ${e.nom} » ?\nCette action est irréversible.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/emplacements/${e.id}`);
      toast.success('Emplacement supprimé');
      onClose?.();
      onReload?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de suppression'); }
    finally { setDeleting(false); }
  };

  const currentAffiche = affiches[0]; // liste triée par uploaded_at desc côté backend
  const affichesUrl = currentAffiche?.image_data || '';
  const affichesEntreprise = currentAffiche?.entreprise || contrat?.entreprise || '';

  const onUploadAffiche = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image trop volumineuse (max 8 Mo)'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.post(`${API}/api/emplacements/${e.id}/affiches`, {
          image_data: ev.target?.result,
          entreprise: contrat?.entreprise || '',
          filename: file.name,
        });
        toast.success('Affiche mise à jour');
        const r = await axios.get(`${API}/api/emplacements/${e.id}/affiches`);
        setAffiches(r.data || []);
      } catch { toast.error('Erreur upload'); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed right-0 top-0 h-full w-full sm:w-[420px] bg-white border-l shadow-2xl z-40 overflow-y-auto" data-testid="emplacement-detail-panel">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
        <h3 className="font-bold text-slate-900">Détails de l&apos;emplacement</h3>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-detail-panel"><X className="h-4 w-4" /></Button>
      </div>

      <div className="p-5 space-y-5">
        {/* Nom + Type */}
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: color }}>{e.numero}</div>
            {editing ? (
              <div className="flex-1 flex gap-2 items-center">
                <input
                  value={editNom}
                  onChange={(ev) => setEditNom(ev.target.value)}
                  className="flex-1 h-9 rounded-md border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  data-testid="edit-nom-input"
                  autoFocus
                />
              </div>
            ) : (
              <>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 truncate">{e.nom}</div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded border ${typeBadgeClass(e.type_panneau)}`}>{e.type_panneau}</span>
              </>
            )}
          </div>
          {editing && (
            <div className="mt-2 flex items-center gap-2">
              <select
                value={editType}
                onChange={(ev) => setEditType(ev.target.value)}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                data-testid="edit-type-select"
              >
                <option value="Panneau déroulant">Panneau déroulant</option>
                <option value="Panneau LED">Panneau LED</option>
                <option value="Grand format">Grand format</option>
                <option value="Panneau poteau">Panneau poteau</option>
              </select>
              <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 gap-1 h-9" data-testid="save-emp-btn">
                <Check className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              <Button size="sm" variant="ghost" className="h-9" onClick={() => { setEditing(false); setEditNom(e.nom); setEditType(e.type_panneau); }} data-testid="cancel-edit-btn">
                Annuler
              </Button>
            </div>
          )}
          <div className="text-sm text-slate-500 mt-2 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Situé à {e.nom.split('(')[0].trim()}, Bangui</div>
        </div>

        {/* Remplissage */}
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900">Remplissage du panneau</div>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
            <div className="text-xs font-semibold text-slate-700 tabular-nums">{occupied} / {cap} affiches</div>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px]">
            <span className="text-violet-600 font-medium">Espace utilisé : {Math.round(pct)}%</span>
            <span className="text-slate-500">Espace restant : {restant} affiche{restant > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Informations techniques */}
        <div className="rounded-xl border border-slate-200">
          <div className="px-4 py-2 border-b border-slate-100 text-sm font-semibold text-slate-900">Informations techniques</div>
          <div className="divide-y divide-slate-100 text-sm">
            {[
              ['Type de panneau', e.type_panneau],
              ['Capacité', `${cap} affiches`],
              ['Dimensions', e.type_panneau === 'Grand format' ? '12m (L) x 4m (H)' : e.type_panneau === 'Panneau LED' ? '6m (L) x 3m (H)' : '4m (L) x 3m (H)'],
              ['Orientation', 'Horizontale'],
              ['Éclairage', e.type_panneau === 'Panneau LED' ? 'Oui (LED)' : 'Oui'],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-4 py-2">
                <span className="text-slate-600">{k}</span>
                <span className="font-medium text-slate-900">{v}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-2">
              <span className="text-slate-600">Statut</span>
              <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Actif</span>
            </div>
          </div>
        </div>

        {/* Entreprise affichée */}
        {contrat && (
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-900 mb-2">Entreprise affichée</div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 font-bold flex items-center justify-center">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 truncate">{contrat.entreprise || 'Client'}</div>
                <div className="text-xs text-slate-500 truncate">{contrat.secteur ? `Secteur : ${contrat.secteur}` : 'Contrat actif'}</div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Contrat actif</Badge>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs text-slate-600">
              <div>Période du contrat : <span className="text-slate-900 font-medium">{contrat.date_debut?.slice(0, 10)}</span> au <span className="text-slate-900 font-medium">{contrat.date_fin?.slice(0, 10) || '—'}</span></div>
              <div>Référence contrat : <span className="text-slate-900 font-medium">{contrat.numero_contrat || contrat.id?.slice(0, 8)}</span></div>
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full gap-2" onClick={() => (window.location.href = `/contrats?id=${contrat.id}`)} data-testid="view-contrat-link">
              <FileText className="h-4 w-4" /> Voir le contrat
            </Button>
          </div>
        )}

        {/* Contrats actifs sur ce panneau (temps réel) */}
        <div className="rounded-xl border border-slate-200 p-4" data-testid="active-contracts-block">
          <div className="text-sm font-semibold text-slate-900 mb-2 flex items-center justify-between">
            <span>Entreprises actives sur ce panneau</span>
            <Badge className="bg-blue-100 text-blue-700 border-blue-300 text-[10px]">{occupied}/{cap} affiches</Badge>
          </div>
          {(!e.occupants || e.occupants.length === 0) ? (
            <div className="text-xs text-slate-500 italic">Aucun contrat actif — {cap} affiches disponibles</div>
          ) : (
            <div className="space-y-2">
              {e.occupants.map((o, idx) => {
                const daysLeft = (() => {
                  if (!o.date_fin) return null;
                  const df = new Date(o.date_fin);
                  const now = new Date();
                  return Math.max(0, Math.ceil((df - now) / 86400000));
                })();
                return (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-200">
                    <div className="w-8 h-8 rounded bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{o.entreprise}</div>
                      <div className="text-[11px] text-slate-500">
                        {o.affiches} affiche{o.affiches > 1 ? 's' : ''} · Jusqu&apos;au {o.date_fin || '—'}
                        {daysLeft !== null && (
                          <span className={`ml-1 font-semibold ${daysLeft < 30 ? 'text-red-600' : daysLeft < 90 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            ({daysLeft} j restants)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Affiche actuelle */}
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-900">Affiche actuelle ({occupied}/{cap})</div>
            {affichesEntreprise && <span className="text-[11px] text-slate-500">{affichesEntreprise}</span>}
          </div>
          {affichesUrl ? (
            <img src={affichesUrl} alt="Affiche" className="w-full h-40 rounded-lg object-cover border border-slate-200" data-testid="current-affiche-image" />
          ) : (
            <div className="w-full h-40 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-sm gap-2" data-testid="no-affiche">
              <ImageIcon className="h-5 w-5" /> Aucune affiche visuelle
            </div>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="cursor-pointer" data-testid="change-affiche-label">
              <div className="inline-flex w-full items-center justify-center gap-2 h-9 px-3 rounded-md border text-sm hover:bg-slate-50">
                <ImageIcon className="h-4 w-4" /> {uploading ? 'Envoi…' : 'Changer l\u2019affiche'}
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={(ev) => onUploadAffiche(ev.target.files?.[0])} data-testid="upload-affiche-input" />
            </label>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9"
              disabled={!affichesUrl}
              onClick={() => affichesUrl && window.open(affichesUrl, '_blank')}
              data-testid="preview-affiche"
            >
              <Maximize2 className="h-4 w-4" /> Aperçu plein écran
            </Button>
          </div>
        </div>

        {/* Historique des affiches */}
        <div className="rounded-xl border border-slate-200">
          <div className="px-4 py-2 border-b border-slate-100 text-sm font-semibold text-slate-900">Historique des affiches</div>
          {reservations.length === 0 ? (
            <div className="px-4 py-4 text-sm text-slate-500">Aucun historique pour ce panneau.</div>
          ) : (
            <div className="divide-y divide-slate-100 text-xs">
              <div className="grid grid-cols-3 gap-2 px-4 py-2 text-slate-500 font-semibold uppercase text-[10px]">
                <span>Période</span><span>Entreprise</span><span>Contrat</span>
              </div>
              {reservations.slice(-6).map((r) => (
                <div key={r.id} className="grid grid-cols-3 gap-2 px-4 py-2 text-slate-700">
                  <span className="truncate">{(r.date_debut || '').slice(0, 10)} – {(r.date_fin || '').slice(0, 10)}</span>
                  <span className="truncate">{r.entreprise || '—'}</span>
                  <span className="truncate">{r.contrat_numero || r.contrat_id?.slice(0, 8) || '—'}</span>
                </div>
              ))}
            </div>
          )}
          <div className="px-4 py-2 border-t border-slate-100 text-right">
            <button className="text-xs text-blue-600 hover:underline" data-testid="view-history">Voir tout l&apos;historique</button>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            className="w-full gap-2 h-11 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => setEditing(true)}
            disabled={editing}
            data-testid="edit-emplacement-btn"
          >
            <Pencil className="h-4 w-4" /> Modifier
          </Button>
          <Button
            variant="outline"
            className="w-full gap-2 h-11 border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={deleteEmp}
            disabled={deleting}
            data-testid="delete-emplacement-btn"
          >
            <Trash2 className="h-4 w-4" /> {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
